import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface AnalyticsContextType {
  isAnalyticsEnabled: boolean;
}

// Define gtag function type
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    [key: string]: any; // Allow indexing with strings for ga-disable properties
    __gsi_config?: {
      cookie_flags: string;
      cookie_update: boolean;
      cookie_expires: number;
      cookie_domain: string;
    };
  }
}

const AnalyticsContext = createContext<AnalyticsContextType>({ isAnalyticsEnabled: false });

export const useAnalytics = () => useContext(AnalyticsContext);

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

// Removed sensitive routes restriction for permissive settings
const SENSITIVE_ROUTES: string[] = [];

// Analytics configuration
const GA4_MEASUREMENT_ID = 'G-B9FPPK93N8';
const GOOGLE_ADS_ID = 'AW-16676678730';

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const location = useLocation(); 
  const [isAnalyticsEnabled, setIsAnalyticsEnabled] = useState(true); // Default to enabled for permissive settings

  // Initialize consent mode v2 with permissive settings
  useEffect(() => {
    // Skip if gtag script is already loaded from HTML
    if (document.querySelector(`script[src*="gtag/js"]`)) {
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;

    // Set permissive consent mode configuration
    gtag('consent', 'default', {
      'ad_storage': 'granted',
      'analytics_storage': 'granted',
      'functionality_storage': 'granted',
      'personalization_storage': 'granted',
      'security_storage': 'granted',
      'wait_for_update': 100
    });

    // Configure permissive cookie settings
    const configScript = document.createElement('script');
    configScript.text = `
      window['ga-disable-${GA4_MEASUREMENT_ID}'] = false;
      window['ga-disable-${GOOGLE_ADS_ID}'] = false;
      
      // Configure permissive cookies
      window.__gsi_config = {
        cookie_flags: 'SameSite=Lax',
        cookie_update: true,
        cookie_expires: 94608000, // 3 years
        cookie_domain: '.' + window.location.hostname.replace(/^www\./, '')
      };
    `;
    document.head.appendChild(configScript);

    return () => {
      if (document.head.contains(configScript)) {
        document.head.removeChild(configScript);
      }
    };
  }, []);

  useEffect(() => {
    // Enable analytics on all pages for permissive settings
    setIsAnalyticsEnabled(true);
  }, [location]);

  // Load and configure analytics with permissive settings
  useEffect(() => {
    if (!isAnalyticsEnabled) return;

    const loadAnalytics = async () => {
      // Skip if gtag is already loaded from HTML
      if (document.querySelector(`script[src*="gtag/js"]`)) {
        // Configure existing gtag with permissive settings
        if (window.gtag) {
          // GA4 Configuration with permissive settings
          window.gtag('config', GA4_MEASUREMENT_ID, {
            'cookie_domain': 'auto',
            'cookie_flags': 'SameSite=Lax',
            'cookie_expires': 94608000,
            'allow_google_signals': true,
            'allow_ad_personalization_signals': true,
            'restricted_data_processing': false,
            'anonymize_ip': false,
            'client_storage': 'cookies',
            'first_party_collection': true,
            'send_page_view': true
          });

          // Update consent to permissive settings
          window.gtag('consent', 'update', {
            'ad_storage': 'granted',
            'analytics_storage': 'granted',
            'functionality_storage': 'granted',
            'personalization_storage': 'granted',
            'security_storage': 'granted'
          });

          // Enable enhanced tracking
          window.gtag('set', {
            'ads_data_redaction': false,
            'url_passthrough': false,
            'allow_ad_personalization_signals': true,
            'allow_google_signals': true
          });
        }
        return;
      }

      // Load GA4 if not already loaded
      const ga4Script = document.createElement('script');
      ga4Script.async = true;
      ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;

      const configScript = document.createElement('script');
      configScript.text = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());

        // GA4 Configuration with permissive settings
        gtag('config', '${GA4_MEASUREMENT_ID}', {
          'cookie_domain': 'auto',
          'cookie_flags': 'SameSite=Lax',
          'cookie_expires': 94608000,
          'allow_google_signals': true,
          'allow_ad_personalization_signals': true,
          'restricted_data_processing': false,
          'anonymize_ip': false,
          'client_storage': 'cookies',
          'first_party_collection': true,
          'send_page_view': true
        });

        // Enable enhanced tracking
        gtag('set', {
          'ads_data_redaction': false,
          'url_passthrough': false,
          'allow_ad_personalization_signals': true,
          'allow_google_signals': true
        });
      `;

      // Add scripts to head
      document.head.appendChild(ga4Script);
      document.head.appendChild(configScript);

      // Update consent with permissive settings
      const updateConsent = () => {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'analytics_storage': 'granted',
          'functionality_storage': 'granted',
          'personalization_storage': 'granted',
          'security_storage': 'granted'
        });

        // Enable all tracking
        window['ga-disable-' + GA4_MEASUREMENT_ID] = false;
        window['ga-disable-' + GOOGLE_ADS_ID] = false;
      };

      // Wait for GA4 to load then update consent
      ga4Script.onload = updateConsent;

      return () => {
        if (document.head.contains(ga4Script)) {
          document.head.removeChild(ga4Script);
        }
        if (document.head.contains(configScript)) {
          document.head.removeChild(configScript);
        }
      };
    };

    loadAnalytics();
  }, [isAnalyticsEnabled]);

  // Lazy load Yandex Metrika
  useEffect(() => {
    if (!isAnalyticsEnabled) return;

    const loadYandexMetrika = async () => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.text = `
        (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
        m[i].l=1*new Date();
        for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
        k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
        (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
        ym(98042431, "init", {
          clickmap:true,
          trackLinks:true,
          accurateTrackBounce:true,
          webvisor:true
        });
      `;

      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    };

    loadYandexMetrika();
  }, [isAnalyticsEnabled]);

  return (
    <AnalyticsContext.Provider value={{ isAnalyticsEnabled }}>
      {children}
    </AnalyticsContext.Provider>
  );
}; 